// Eric (2026-02-06) — Page publique de capture de leads. Partageable via
// le lien knoria.com/demo. Pas d'auth requise. Vise les secteurs régulés
// (assurance, comptables, planificateurs, courtiers immobiliers, juridique,
// santé). Contact direct par email (mailto:) uniquement.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { PublicLangToggle } from "../components/PublicLangToggle";

const CONTACT_EMAIL = "eric.roy@knoria.com";
// Eric 2026-06-25 (iter123) — WhatsApp retiré : pas de numéro disponible.
// L'unique canal de contact public reste l'email (CONTACT_EMAIL).
const MAILTO_SUBJECT = "Démo Knoria — Demande de rendez-vous";
const MAILTO_BODY = "Bonjour Eric,\n\nJe souhaite voir une démo de Knoria.\n\nMon nom : \nMa firme : \nSecteur : \nNombre de collaborateurs : \nQuelques mots sur mon besoin : \n\nMerci,";

export default function PublicDemoPage() {
  const { tr } = useLang();
  useEffect(() => {
    document.title = "Knoria — Plateforme IA de formation et conformité (Loi 25)";
    // SEO: meta description
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      tr(
        "Knoria transforme tes vidéos de formation en base de connaissances IA conforme Loi 25. Hébergement Canada, BYOK, audit complet. Réserve une démo.",
        "Knoria turns your training videos into a Law 25-compliant AI knowledge base. Canadian hosting, BYOK, full audit trail. Book a demo.",
      )
    );
  }, []);

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-['Outfit']" data-testid="public-demo-page">
      {/* Sticky top nav minimaliste */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight" data-testid="nav-logo">
            <span className="text-[#002FA7]">KNORIA</span>
          </Link>
          <div className="flex items-center gap-3">
            {/* Tâche 1401 — Sans bascule, la version anglaise serait
                inatteignable pour un prospect. */}
            <PublicLangToggle />
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(MAILTO_SUBJECT)}&body=${encodeURIComponent(MAILTO_BODY)}`}
              className="hidden sm:inline text-sm text-zinc-600 hover:text-[#002FA7]"
              data-testid="nav-contact-email"
            >
              {tr("Nous écrire", "Email us")}
            </a>
            <a
              href="#reserver"
              className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#001a5e] text-white text-sm font-semibold transition-colors"
              data-testid="nav-cta-demo"
            >
              {tr("Réserver une démo", "Book a demo")}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div className="grid sm:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              {tr("100 % hébergé au Canada — conforme à la Loi 25", "100% hosted in Canada — Law 25 compliant")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              {tr("Transforme tes ", "Turn your ")}<span className="text-[#002FA7]">{tr("vidéos de formation", "training videos")}</span>{tr(" en plateforme IA conforme.", " into a compliant AI platform.")}
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed mb-8">
              {tr(
                "Importe une vidéo. Knoria en extrait automatiquement les Q&R, les scripts de vente, les procédures et les concepts. Tes employés posent leurs questions à l\u2019IA, qui répond en citant la source vidéo exacte. Tout est audité, conforme et traçable.",
                "Upload a video. Knoria automatically extracts the Q&A, sales scripts, procedures and concepts. Your people ask the AI their questions, and it answers citing the exact video source. Everything is audited, compliant and traceable.",
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#reserver"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#002FA7] hover:bg-[#001a5e] text-white text-base font-semibold transition-colors"
                data-testid="hero-cta-demo"
              >
                {tr("Réserver une démo gratuite →", "Book a free demo →")}
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(MAILTO_SUBJECT)}&body=${encodeURIComponent(MAILTO_BODY)}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-zinc-300 hover:border-[#002FA7] text-zinc-700 hover:text-[#002FA7] text-base font-semibold transition-colors"
                data-testid="hero-cta-email"
              >
                {tr("Nous écrire", "Email us")}
              </a>
            </div>
            <p className="text-xs text-zinc-500 mt-4">
              Démo de 20 minutes · Aucun engagement · Sur Zoom ou en personne (Montréal)
            </p>
          </div>
          {/* Visuel mock dashboard */}
          <div className="relative hidden sm:block" data-testid="hero-visual">
            <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7]/10 via-emerald-100/30 to-amber-100/30 rounded-3xl blur-3xl" />
            <div className="relative bg-white border border-zinc-200 rounded-2xl shadow-2xl overflow-hidden">
              <div className="h-9 bg-zinc-100 border-b border-zinc-200 flex items-center gap-1.5 px-3">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-teal-400" />
                <span className="ml-2 text-[10px] text-zinc-500 font-mono">knoria.com/dashboard</span>
              </div>
              <div className="p-6 space-y-3">
                <div className="h-6 bg-gradient-to-r from-[#002FA7]/20 to-transparent rounded" style={{ width: "60%" }} />
                <div className="grid grid-cols-3 gap-2">
                  <div className="h-16 bg-teal-50 border border-teal-200 rounded-lg p-2">
                    <div className="text-[10px] text-teal-700 font-semibold">Q&R IA</div>
                    <div className="text-xl font-bold text-teal-800">142</div>
                  </div>
                  <div className="h-16 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <div className="text-[10px] text-amber-700 font-semibold">Scripts</div>
                    <div className="text-xl font-bold text-amber-800">38</div>
                  </div>
                  <div className="h-16 bg-violet-50 border border-violet-200 rounded-lg p-2">
                    <div className="text-[10px] text-violet-700 font-semibold">Concepts</div>
                    <div className="text-xl font-bold text-violet-800">67</div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="h-3 bg-zinc-100 rounded" style={{ width: "90%" }} />
                  <div className="h-3 bg-zinc-100 rounded" style={{ width: "75%" }} />
                  <div className="h-3 bg-zinc-100 rounded" style={{ width: "85%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-center text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-8">
            {tr("Conçu pour les secteurs régulés", "Built for regulated industries")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6 text-center">
            {[
              { icon: "🏛️", label: "Cabinets juridiques" },
              { icon: "🛡️", label: "Assurance" },
              { icon: "📊", label: "Comptabilité" },
              { icon: "🏠", label: "Courtage immobilier" },
              { icon: "⚕️", label: "Santé / médical" },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-2">
                <span className="text-3xl">{s.icon}</span>
                <span className="text-sm font-semibold text-zinc-700">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 bénéfices clés */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
          Trois problèmes que Knoria règle <span className="text-[#002FA7]">en 48 heures</span>
        </h2>
        <p className="text-center text-zinc-600 mb-16 max-w-2xl mx-auto">
          La formation interne, la traçabilité réglementaire et l&apos;uniformisation du vocabulaire — trois douleurs qui coûtent cher quand elles s&apos;accumulent.
        </p>
        <div className="grid sm:grid-cols-3 gap-8">
          {[
            {
              icon: "🇨🇦",
              title: tr("Loi 25 native", "Law 25 by design"),
              desc: tr(
                "100 % des données stockées au Canada. Audit complet et restauration de chaque modification — prêt pour un contrôle de la Commission d'accès à l'information.",
                "100% of the data stored in Canada. Full audit trail and restore on every change — ready for a review by Quebec's access-to-information commission.",
              ),
            },
            {
              icon: "🔐",
              title: tr("Forfait AI BYOK", "BYOK AI package"),
              desc: tr(
                "Chaque client branche ses propres clés OpenAI, AssemblyAI et Resend. Tes données n'entrent jamais dans notre comptabilité IA — zéro dépendance technique à Knoria.",
                "Every client plugs in their own OpenAI, AssemblyAI and Resend keys. Your data never enters our AI billing — no technical lock-in to Knoria.",
              ),
            },
            {
              icon: "⚡",
              title: tr("IA contextuelle", "Context-aware AI"),
              desc: tr(
                "Import d'une vidéo → Q&R, scripts, procédures et concepts générés en moins de 5 minutes. Chaque réponse du dialogueur cite l'horodatage exact de la source vidéo.",
                "Upload a video → Q&A, scripts, procedures and concepts generated in under 5 minutes. Every chatbot answer cites the exact timestamp in the source video.",
              ),
            },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-zinc-200 rounded-2xl p-8 hover:border-[#002FA7] hover:shadow-lg transition-all">
              <div className="text-4xl mb-4">{b.icon}</div>
              <h3 className="text-lg font-bold mb-3">{b.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="bg-gradient-to-br from-[#002FA7] to-[#001a5e] text-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            {tr("Comment ça marche", "How it works")}
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Tu uploades tes vidéos", desc: "Ou tu nous envoies tes fichiers. On les importe pour toi. Aucune limite de durée." },
              { step: "2", title: "L'IA extrait tout", desc: "Transcript, Q&R, scripts de vente, procédures, concepts financiers, dictionnaire métier. En quelques minutes." },
              { step: "3", title: "Tes employés s'en servent", desc: "Chatbot avec sources citées, quiz auto-générés, parcours d'apprentissage personnalisés." },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white text-[#002FA7] text-2xl font-bold mb-4">
                  {s.step}
                </div>
                <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                <p className="text-sm text-blue-100 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignage */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <blockquote className="text-center" data-testid="testimonial-block">
          <p className="text-2xl sm:text-3xl font-medium leading-relaxed text-zinc-800 mb-6">
            {tr(
              "« En 48 heures, on a remplacé 6 mois de documentation interne par une plateforme IA qui répond aux questions de mes conseillers — avec la source vidéo en preuve. »",
              "“In 48 hours we replaced six months of internal documentation with an AI platform that answers my advisers' questions — with the video source as proof.”",
            )}
          </p>
          <footer className="text-sm text-zinc-500">
            {tr("— Firme partenaire en conseil financier · Montréal", "— Partner financial advisory firm · Montreal")}
          </footer>
        </blockquote>
      </section>

      {/* Prix sur demande + CTA final */}
      <section id="reserver" className="bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {tr("Prêt à voir Knoria sur tes propres données ?", "Ready to see Knoria on your own data?")}
          </h2>
          <p className="text-lg text-zinc-600 mb-10 leading-relaxed">
            {tr(
              "Démo gratuite de 20 minutes, sans engagement. On configure ensemble ta première vidéo de test pour que tu voies le résultat en direct.",
              "A free 20-minute demo, no strings attached. We set up your first test video together so you see the result live.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(MAILTO_SUBJECT)}&body=${encodeURIComponent(MAILTO_BODY)}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#002FA7] hover:bg-[#001a5e] text-white text-base font-semibold transition-colors shadow-lg shadow-[#002FA7]/20"
              data-testid="footer-cta-email"
            >
              {tr("✉️ Écrire à Eric", "✉️ Email Eric")}
            </a>
            {/* Eric 2026-06-25 (iter123) — Bouton WhatsApp retiré (pas de numéro public). */}
          </div>
          <p className="text-xs text-zinc-500 mt-8">
            {tr("Tarifs sur demande · Setup en 48 heures · Hébergement Montréal", "Pricing on request · Set up in 48 hours · Hosted in Montreal")}
          </p>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-zinc-500">
            © {new Date().getFullYear()} Knoria · Montréal, Québec, Canada
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/" className="hover:text-[#002FA7]">Accueil</Link>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="hover:text-[#002FA7]"
            >
              {CONTACT_EMAIL}
            </a>
            <Link to="/login" className="hover:text-[#002FA7]" data-testid="footer-login-link">Connexion client</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
